import React from 'react';
import { useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  Button,
  Modal,
  BodyText,
  Alert,
} from '@chargify/custom-ui-components';
import { useToastMessages } from '@chargify/toasts';

import { useChangePaymentProfile } from '../../../hooks';
import { changePaymentProfileSelectors } from '../../../machines/changePaymentProfile';

type ChangePaymentProfileProps = {
  /**
   * Product catalog identifier
   */
  catalogId: number;
  /**
   * Payment profile identifier
   */
  paymentProfileId: number;
  /**
   * Subscription identifier
   */
  subscriptionId: number;
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: (successUpdate?: boolean) => void;
};

const Content = ({
  subscriptionId,
  catalogId,
  onClose,
  displayMode,
  paymentProfileId,
}: ChangePaymentProfileProps) => {
  const { t } = useTranslation(['subscriptions-manager', 'payment-profile']);
  const toastService = useToastMessages();

  const onSuccessfulChange = () => {
    toastService.show({
      message: t('payment_profile.success_payment_method_change'),
      level: 'success',
    });
    onClose(true);
  };
  const changePaymentProfileService = useChangePaymentProfile({
    subscriptionId,
    catalogId,
    paymentProfileId,
    onChangeSuccess: onSuccessfulChange,
  });

  const { isError, isLoading } = useSelector(
    changePaymentProfileService,
    changePaymentProfileSelectors.getChangePaymentProfileStatus
  );

  return (
    <Card border={displayMode === 'layer' ? 'sharp' : 'rounded'}>
      <Card.Header>
        <Modal.Title
          onClose={onClose}
          title={t('payment_profile.change_payment_method')}
        />
      </Card.Header>
      <Card.Body>
        {isError ? (
          <Alert color="danger" title={t('payment_profile.unexpected_problem')}>
            {t('payment_profile.unable_to_change_payment_method')}
          </Alert>
        ) : (
          <BodyText>
            {t('payment_profile.payment_method_change_confirmation')}
          </BodyText>
        )}
      </Card.Body>
      <Card.Footer>
        <Button.Group alignment="right">
          <Button
            color="secondary"
            label={t('cancel', { ns: ['common'] })}
            onClick={() => onClose(false)}
            disabled={isLoading}
          />
          <Button
            color="primary"
            label={t('payment_profile.payment_method_change_cta')}
            isLoading={isLoading}
            disabled={isLoading || isError}
            onClick={() =>
              changePaymentProfileService.send({
                type: 'CHANGE_PAYMENT_PROFILE',
                paymentProfileId,
              })
            }
          />
        </Button.Group>
      </Card.Footer>
    </Card>
  );
};

export default Content;
